import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { dapi as api } from "./deskApi";

// Pictures found in the PDF; tap one to have the AI read it.
export const PicturePicker = ({ images, onPick }) => {
  if (!images.length) return <p className="text-sm text-slate-500 p-4" data-testid="pdf-ai-no-images">No pictures were found in this PDF. Use “Page area” to pick any part of a page.</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-1" data-testid="pdf-ai-image-grid">
      {images.map((im) => (
        <button key={`${im.page}-${im.xref}`} type="button" onClick={() => onPick({ page: im.page, xref: im.xref })}
          className="rounded border border-slate-200 hover:border-sky-500 hover:bg-sky-50 p-2 text-left transition-colors"
          data-testid={`pdf-ai-image-${im.page}-${im.xref}`}>
          <div className="h-28 grid place-items-center bg-[repeating-conic-gradient(#f1f5f9_0_25%,#fff_0_50%)] bg-[length:16px_16px] rounded">
            <img src={im.thumb} alt="" className="max-h-28 max-w-full object-contain" />
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Page {im.page + 1} · {im.width}×{im.height}</div>
        </button>
      ))}
    </div>
  );
};

// Drag a box over any part of a page.
export const AreaPicker = ({ nid, pages, onPick }) => {
  const [page, setPage] = useState(0);
  const [render, setRender] = useState(null);
  const [box, setBox] = useState(null);
  const start = useRef(null);
  const imgRef = useRef(null);
  useEffect(() => {
    setRender(null); setBox(null);
    api.get(`/desktop/fs/${nid}/pdf-page/${page}`).then(({ data }) => setRender(data)).catch(() => setRender(false));
  }, [nid, page]);
  const point = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    return { x: Math.max(0, Math.min(r.width, e.clientX - r.left)), y: Math.max(0, Math.min(r.height, e.clientY - r.top)) };
  };
  const down = (e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); start.current = point(e); setBox(null); };
  const move = (e) => {
    if (!start.current) return;
    const p = point(e), s = start.current;
    setBox({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const up = () => { start.current = null; };
  const use = () => {
    const r = imgRef.current.getBoundingClientRect();
    const sx = render.width / r.width, sy = render.height / r.height;
    onPick({ page, rect: [box.x * sx, box.y * sy, (box.x + box.w) * sx, (box.y + box.h) * sy] });
  };
  return (
    <div className="space-y-2" data-testid="pdf-ai-area-picker">
      <div className="flex items-center gap-2 text-sm">
        <span>Page</span>
        <select value={page} onChange={(e) => setPage(Number(e.target.value))} className="border border-slate-300 rounded px-2 py-1" data-testid="pdf-ai-area-page">
          {pages.map((p) => <option key={p.page} value={p.page}>{p.page + 1}</option>)}
        </select>
        <span className="text-xs text-slate-500">Drag a box around the words you want to change.</span>
        <button type="button" disabled={!box || box.w < 6 || box.h < 6} onClick={use}
          className="ml-auto rounded bg-sky-600 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40" data-testid="pdf-ai-area-use">Read this area</button>
      </div>
      <div className="max-h-[60vh] overflow-auto rounded border border-slate-200 bg-slate-100 grid place-items-center">
        {render === null && <Loader2 className="w-6 h-6 animate-spin text-slate-400 m-10" />}
        {render === false && <p className="text-sm text-red-600 p-6">This page couldn&apos;t be shown.</p>}
        {render && (
          <div className="relative select-none touch-none cursor-crosshair" onPointerDown={down} onPointerMove={move} onPointerUp={up} data-testid="pdf-ai-area-canvas">
            <img ref={imgRef} src={render.image} alt={`Page ${page + 1}`} draggable={false} className="block max-w-full" />
            {box && <div className="absolute border-2 border-sky-500 bg-sky-400/15" style={{ left: box.x, top: box.y, width: box.w, height: box.h }} />}
          </div>
        )}
      </div>
    </div>
  );
};
